import { useEffect, useRef, useMemo, MutableRefObject } from 'react';
import * as d3 from 'd3-selection';
import * as d3Zoom from 'd3-zoom';
import 'd3-dispatch';

import { useFieldViewport, clampZoomTransform } from '@labradorsports/play-rendering';

import { useFieldContainer } from '../providers/index.js';

const arrayFromTouches = (touches: TouchList): any[] => {
    return new Array(touches.length).fill(null).map((_, index) => touches.item(index));
};

interface Props {
    dragSubjectRef: MutableRefObject<any>;
}

export default function useZoom({ dragSubjectRef }: Props) {
    const { containerRef } = useFieldContainer();
    const { fieldViewport, setZoomTransform, zoomTransform, zoomLevels } = useFieldViewport();

    const zoomHandler = useRef<AnyFunction>();
    const zoomConstrain = useRef<AnyFunction>();

    const maxZoomLevel = zoomLevels[zoomLevels.length - 1];

    zoomHandler.current = (event) => {
        setZoomTransform(event.transform);
    };

    zoomConstrain.current = (transform) => {
        return clampZoomTransform(fieldViewport, transform.k, maxZoomLevel, transform);
    };

    const zoom = useMemo(() => {
        return d3Zoom
            .zoom()
            .constrain((transform) => {
                return zoomConstrain.current(transform);
            })
            .filter((event) => {
                return (
                    (!event.touches ||
                        !arrayFromTouches(event.touches).some((touch) => touch.touchType === 'stylus')) &&
                    !dragSubjectRef.current
                );
            })
            .on('zoom', (event) => {
                zoomHandler.current(event);
            });
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            const ref = containerRef.current;
            zoom.scaleExtent([1, maxZoomLevel]);
            d3.select(ref).call(zoom);

            return () => {
                d3.select(ref).on('.zoom', null);
            };
        }

        return () => {
            //
        };
    }, [containerRef.current, maxZoomLevel]);

    useEffect(() => {
        if (containerRef.current) {
            zoom.transform(d3.select(containerRef.current), zoomTransform);
        }
    }, [containerRef.current, zoomTransform.k, zoomTransform.x, zoomTransform.y]);
}
